import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography } from '@mui/material';
import {
  CloseOutlined,
  FileUploadOutlined,
  InfoOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { claimsActions } from '../../../../../store';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_GRAY2,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { ClaimStatus } from '../constants';
import {
  FHButton,
  FHConfirmDialog,
  FHDropzone,
  FHPdfViewer,
  FHTooltip,
  FProgressBar,
} from '../../../../base';
import FHPreviewDialog from '../../../../base/dialogs/FHPreviewDialog';
import { Themes } from '../../../../../constants';

import type { RootState } from '../../../../../store';

interface IEligibilityOfBenefitsProps {
  isEditingInfo: boolean;
}

function EligibilityOfBenefits({ isEditingInfo }: IEligibilityOfBenefitsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [currentPolicyId, setCurrentPolicyId] = useState<number | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<number | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );

  const disableUploadButton =
    showUploadDialog ||
    fileUploading ||
    claimsLoading ||
    !(claim?.status === ClaimStatus.APPROVED || claim?.status === ClaimStatus.FULL_DENIAL);

  const handleFileUpload = async (file: File) => {
    if (!file || !claim || !currentPolicyId) return;

    let documentId: number | null = null;

    try {
      const response = await dispatch(
        claimsActions.createEobDocument({
          claimId: claim.id,
          data: { policy_id: currentPolicyId, file_name: file.name },
          getAccessTokenSilently,
        }),
      ).unwrap();

      setFileUploading(true);
      const { presigned_url_upload: presignedUploadUrl, id: docId } = response;
      documentId = docId;

      if (!presignedUploadUrl) {
        toast.error('Error uploading file - document URL not found');
        throw new Error('Document URL not found');
      }
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(presignedUploadUrl, {
        method: 'PUT',
        body: file,
        headers: { Accept: 'application/json' },
      });

      if (!uploadResponse.ok) {
        throw new Error('Error uploading file');
      }

      toast.success('EOB document uploaded successfully');
    } catch (error: any) {
      toast.error(`Error uploading file: ${error.message}`);
      console.error(error);
      // Call handleFailedUpload if documentId is available and upload fails
      if (documentId) {
        await handleFailedUpload(documentId);
      }
    } finally {
      setFileUploading(false);
      setShowUploadDialog(false);
      setCurrentPolicyId(null);
    }
  };

  //Delete the pre-signed URL in case of failed upload
  const handleFailedUpload = async (documentId: number) => {
    if (claim?.id && documentId) {
      await dispatch(
        claimsActions.deleteEobDocument({
          claimId: claim.id,
          eobDocumentId: documentId,
          getAccessTokenSilently,
        }),
      );
    }
  };

  const handleSelectFile = (files: File[]) => {
    const selectedFile = files[0];
    if (selectedFile) handleFileUpload(selectedFile);
  };

  const handleDelete = async () => {
    if (!claim?.id || !currentDocumentId) return;
    setShowDeleteDialog(false);
    try {
      const res = await dispatch(
        claimsActions.deleteEobDocument({
          claimId: claim.id,
          eobDocumentId: currentDocumentId,
          getAccessTokenSilently,
        }),
      );
      if (!res.error) {
        toast.success('EOB document deleted successfully');
      }
    } catch (_error) {
      toast.error('Error deleting document');
    }
  };

  const handlePreview = (url: string) => {
    setPdfPreviewUrl(url);
    setShowPdfPreview(true);
  };

  return (
    <Box data-testid="eligibilityOfBenefitsContainer" mb={1}>
      {fileUploading && <FProgressBar />}
      <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mb={2}>
        Explanation of benefits
      </Typography>
      {claim?.policies?.map((policy, index) => (
        <Grid container spacing={2} alignItems={'flex-start'} mb={3} key={index}>
          <Grid item md={3} sm={6} xs={12}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Policy
            </Typography>
            <Typography textAlign="left" mb={1}>
              {policy.payor_responsibility} ({policy.billing_payor.label})
            </Typography>
          </Grid>
          <Grid item md={9} sm={6} xs={12}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Explanation of benefits
            </Typography>
            <Box
              sx={{
                display: { xs: 'block', sm: 'block', md: 'flex' },
                alignItems: 'center',
                gap: 2,
              }}
            >
              {claim?.eob_documents?.some((doc) => doc.policy.id === policy.id)
                ? claim.eob_documents
                    .filter((doc) => doc.policy.id === policy.id)
                    .map((document, index) => (
                      <Box
                        data-testid={`uploadedFile_${document.file_name}`}
                        title={document.file_name}
                        key={index}
                        sx={{
                          width: '300px',
                          height: '40px',
                          padding: '8px',
                          backgroundColor: FORTA_WHITE,
                          borderRadius: '8px',
                          display: 'flex',
                          gap: 1,
                          mb: 2,
                          cursor: 'pointer',
                        }}
                        onClick={() => handlePreview(document.document_url)}
                      >
                        <PictureAsPdfOutlined sx={{ color: FORTA_BLUE.primary }} />
                        <Typography
                          sx={{
                            textAlign: 'left',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%',
                          }}
                        >
                          {document.file_name}
                        </Typography>
                        {isEditingInfo && (
                          <CloseOutlined
                            sx={{
                              color: FORTA_BLUE.primary,
                              cursor: 'pointer',
                              fontSize: '24px',
                              fontWeight: 400,
                              marginLeft: 'auto',
                              marginTop: 'auto',
                              '&:hover': {
                                transition: 'all 0.3s',
                                backgroundColor: FORTA_GRAY2,
                                borderRadius: '50%',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentDocumentId(document.id);
                              setShowDeleteDialog(true);
                            }}
                            data-testid="eobCloseIcon"
                          />
                        )}
                      </Box>
                    ))
                : !isEditingInfo && (
                    <Typography textAlign="left">No documents uploaded for this policy</Typography>
                  )}
            </Box>
            {isEditingInfo &&
            claim?.eob_documents?.filter((doc) => doc.policy.id === policy.id).length < 3 ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-start' }}
              >
                <Typography textAlign="left">
                  Only .pdf files less than 10 MB are accepted
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FHButton
                    secondary
                    startIcon={<FileUploadOutlined />}
                    disabled={disableUploadButton}
                    onClick={() => {
                      setCurrentPolicyId(policy.id);
                      setShowUploadDialog(true);
                    }}
                    data-testid={`${policy.payor_responsibility}_upload_eob_doc_btn`}
                  >
                    Upload
                  </FHButton>
                  {!(
                    claim?.status === ClaimStatus.APPROVED ||
                    claim?.status === ClaimStatus.FULL_DENIAL
                  ) && (
                    <FHTooltip
                      arrow
                      title={
                        'Claim status must be either Approved or Full Denial to upload EOB documents'
                      }
                      placement="right-end"
                      sx={{
                        borderRadius: '10px',
                        display: 'flex',
                        maxWidth: '350px',
                      }}
                      tooltipbackground={FORTA_WHITE}
                      tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                    >
                      <InfoOutlined sx={{ color: FORTA_ORANGE }} />
                    </FHTooltip>
                  )}
                </Box>
              </Box>
            ) : (
              isEditingInfo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography textAlign="left" color={FORTA_ERROR}>
                    Only 3 EOB documents can be uploaded per policy
                  </Typography>
                </Box>
              )
            )}
            <FHConfirmDialog
              open={showDeleteDialog}
              colorTheme={Themes.Primary}
              title={'Delete document'}
              content={
                'Are you sure you want to delete this EOB document? This action cannot be undone.'
              }
              submittext={'Delete'}
              canceltext={'Cancel'}
              submitBtnTestId={'eobCloseConfimationBtn'}
              cancelBtnTestId={'eobCloseCancelBtn'}
              onClose={() => setShowDeleteDialog(false)}
              onSubmit={handleDelete}
            />
            <FHPreviewDialog
              open={showPdfPreview}
              title={'Document preview'}
              contentRenderer={() => <FHPdfViewer fileUrl={pdfPreviewUrl} />}
              onClose={() => setShowPdfPreview(false)}
            />
            <FHDropzone
              colorTheme={Themes.Primary}
              show={showUploadDialog}
              fileNameRegEx={'^[a-zA-Z0-9_., -]*$'}
              maxFiles={1}
              maxSize={10}
              accept={{
                'application/pdf': ['.pdf'],
              }}
              validateFilename={false}
              noPreviewFiles={['.pdf']}
              title={'Upload Document'}
              subTitle={'Upload the EOB document for the claim'}
              selectButtontext="Upload Document"
              onSelect={(files) => handleSelectFile(files)}
              onClose={() => {
                setShowUploadDialog(false);
                setCurrentPolicyId(null);
              }}
              browseButtonTestId={`browseButton_${index}`}
              uploadButtonTestId={`uploadButton_${index}`}
            />
          </Grid>
        </Grid>
      ))}
    </Box>
  );
}

export default EligibilityOfBenefits;
